import { useDispatch, useSelector } from "react-redux";
import { cleanCurrentUser, getUserByIdApi, upsertUserApi } from "../state/action";
import { useNavigate, useParams } from "react-router";
import { RootState } from "src/state/reducer";
import { serviceLoading } from "src/common/apiLoader/state/selection";
import { GET_USER_BY_ID_API, UPSERT_USER_API } from "../state/actionTypes";
import { UserError } from "../state/types";
import { User } from "src/common/models/user";
import { PageCard } from "src/common/styles/styles";
import { SystemDescriptions } from "src/common/descriptions";
import InnerLoader from "src/components/InnerLoader";
import { UserForm } from "../UserForm";
import { CancelConfirmationModal } from "src/common/descriptions/components/cancelConfirmationModal";
import { ModalSuccess } from "src/components/ModalSuccess";
import { useEffect, useState } from "react";
import { Urls } from "src/common/urls";
import { Loader } from "src/components/Loader";
import { Button, Dropdown, MenuProps, Space, notification } from "antd";
import { DownOutlined, EditOutlined } from '@ant-design/icons';
import { UserPasswordAssignation } from "../PasswordAssignation";
import { PasswordReset } from "../PasswordReset";

interface ReduxProps {
    error?: UserError
    isUpsertingUser: boolean
    upsertUserSuccess: boolean
    isGettingUser: boolean
    currentUser?: User
    user?: User
}

export const CreateUser = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const { id: userId } = useParams<{ id: string }>();

    const [modalConfirmationOpen, setModalConfirmationOpen] = useState<boolean>(false)
    const [modalCancelOpen, setModalCancelOpen] = useState<boolean>(false)
    const [formValues, setFormValues] = useState<User | undefined>()
    const [actionButtonClicked, setActionButtonClicked] = useState<boolean>(false)
    const [assignPasswordOpen, setAssignPasswordOpen] = useState<boolean>(false)
    const [resetPasswordOpen, setResetPasswordOpen] = useState<boolean>(false)

    useEffect(() => {
        if (userId) {
            dispatch(getUserByIdApi(Number(userId)))
        }
    }, [])

    const descriptions = SystemDescriptions.PAGES.USER.CREATE

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        error: state?.user.error,
        isUpsertingUser: serviceLoading(state, [UPSERT_USER_API]),
        upsertUserSuccess: state.user.upsertUserSuccess,
        isGettingUser: serviceLoading(state, [GET_USER_BY_ID_API]),
        currentUser: state.user.currentUser,
        user: state.security.user,
    }))

    const handleConfirmationAccept = () => {
        setModalConfirmationOpen(false)
        setActionButtonClicked(true)
        dispatch(upsertUserApi(formValues as User))
    }

    const handleCancelAccept = () => {
        navigate(Urls.FRONTEND.USER.USERS)
    }

    const renderModals = () => (
        <>
            <CancelConfirmationModal
                isVisible={modalConfirmationOpen}
                onAccept={handleConfirmationAccept}
                onCancel={() => {
                    setModalConfirmationOpen(false)
                }}
                title={descriptions.CONFIRM_MODAL.TITLE}
                okText={descriptions.CONFIRM_MODAL.ACTION_BUTTON}
                cancelText={descriptions.CONFIRM_MODAL.CANCEL_BUTTON}
                description={descriptions.CONFIRM_MODAL.DESCRIPTION}
            />
            <CancelConfirmationModal
                isVisible={modalCancelOpen}
                onAccept={handleCancelAccept}
                onCancel={() => { setModalCancelOpen(false) }}
                title={descriptions.CANCEL_MODAL.TITLE}
                okText={descriptions.CANCEL_MODAL.ACTION_BUTTON}
                cancelText={descriptions.CANCEL_MODAL.CANCEL_BUTTON}
                description={descriptions.CANCEL_MODAL.DESCRIPTION}
            />
            <ModalSuccess
                isVisible={(reduxProps.upsertUserSuccess) && actionButtonClicked}
                title={descriptions.SUCCESS_MODAL.TITLE}
                description={descriptions.SUCCESS_MODAL.DESCRIPTION}
                onPrimaryAction={handleCancelAccept}
                onSecondaryActiont={handleCancelAccept}
                primaryLabel={descriptions.SUCCESS_MODAL.PRIMARY_LABEL}
                secondaryLabel={descriptions.SUCCESS_MODAL.SECONDARY_LABEL}
                hideSecondaryButton={true}
            />
            {
                reduxProps.currentUser && assignPasswordOpen &&
                <UserPasswordAssignation
                    open={assignPasswordOpen}
                    onAccept={() => {
                        dispatch(cleanCurrentUser())
                        setAssignPasswordOpen(false)
                        openNotification(
                            descriptions.PASSWORD_UPDATE_SUCCESS_TITLE,
                            descriptions.PASSWORD_UPDATE_SUCCESS_DESCRIPTION,
                        )
                        navigate(Urls.FRONTEND.USER.USERS)
                    }}
                    onCancel={(errorMessage?: string) => {
                        dispatch(cleanCurrentUser())
                        setAssignPasswordOpen(false)
                        if (errorMessage) {
                            openNotification(
                                descriptions.PASSWORD_UPDATE_ERROR_TITLE,
                                errorMessage,
                            )
                        }
                        navigate(Urls.FRONTEND.USER.USERS)
                    }}
                    user={reduxProps.currentUser}
                />
            }
            {
                reduxProps.currentUser && resetPasswordOpen &&
                <PasswordReset
                    open={resetPasswordOpen}
                    onAccept={() => {
                        dispatch(cleanCurrentUser())
                        setResetPasswordOpen(false)
                        openNotification(
                            descriptions.PASSWORD_UPDATE_SUCCESS_TITLE,
                            descriptions.PASSWORD_UPDATE_SUCCESS_DESCRIPTION,
                        )
                        // TODO: MAYBE ADD A ALERT BEFORE LOGGING OUT
                        navigate(Urls.FRONTEND.LOGOUT)
                    }}
                    onCancel={() => {
                        dispatch(cleanCurrentUser())
                        setResetPasswordOpen(false)
                    }}
                    user={reduxProps.currentUser}
                />
            }
        </>
    )

    const openNotification = (message: string, description: string) => {
        notification.success({
            message,
            description,
        });
    };

    const onFinish = (user: Partial<User>) => {
        setFormValues(user as User)
        setModalConfirmationOpen(true)
    }

    const onCancel = () => {
        setModalCancelOpen(true)
    }

    const renderLoader = () => (
        <Loader
            isVisible={reduxProps.isUpsertingUser}
            title={descriptions.MAIN_ACTION_TITLE}
            description={descriptions.MAIN_ACTION_DESCRIPTION}
        />
    )

    const handleMenuClick: MenuProps['onClick'] = (e) => {
        if (e.key === "ASSIGN_PASSWORD") {
            setAssignPasswordOpen(true)
        } else if (e.key === "PASSWORD_CHANGE") {
            setResetPasswordOpen(true)
        }
    };

    const items: MenuProps['items'] = [];

    if (reduxProps.user && Number(reduxProps.user?.id) === Number(userId)) {
        items.push({
            label: descriptions.MORE_OPTIONS.RESET_PASSWORD,
            key: 'PASSWORD_CHANGE',
            icon: <EditOutlined />,
        })
    } else {
        items.push({
            label: descriptions.MORE_OPTIONS.ASSIGN_NEW_PASSWORD,
            key: 'ASSIGN_PASSWORD',
            icon: <EditOutlined />,
        })
    }

    const menuProps = {
        items,
        onClick: handleMenuClick,
    };

    const renderExtraContent = () => {
        return <Dropdown menu={menuProps}>
            <Button size="small">
                <Space>
                    {descriptions.MORE_OPTIONS.TITLE}
                    <DownOutlined />
                </Space>
            </Button>
        </Dropdown>
    }

    return (
        <>
            <PageCard
                size="small"
                title={reduxProps.currentUser ? descriptions.EDIT_TITLE : descriptions.TITLE}
                extra={renderExtraContent()}
            >
                {
                    reduxProps.isGettingUser
                        ? <InnerLoader />
                        : <UserForm
                            onFinish={onFinish}
                            onCancel={onCancel}
                            initialValues={reduxProps.currentUser}
                        />
                }
            </PageCard>
            {renderModals()}
            {renderLoader()}
        </>
    )
}
